import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/store';
import { categoryMappingActions } from '../reducer';
import {
  selectCategoryMappingIsReady,
  selectIdToSlugMap,
  selectSlugToIdMap,
  idsToSlugs as idsToSlugsUtil,
  slugsToIds as slugsToIdsUtil,
} from '../selectors';
import useCategories from './useCategories';

/**
 * Hook for bidirectional mapping between category IDs and URL-friendly slugs.
 */
export default function useCategoryMapping() {
  const dispatch = useAppDispatch();
  const { data: categories, isSuccess } = useCategories();

  const isReady = useAppSelector(selectCategoryMappingIsReady);
  const idToSlugMap = useAppSelector(selectIdToSlugMap);
  const slugToIdMap = useAppSelector(selectSlugToIdMap);

  // Sync categories to Redux when they're loaded
  useEffect(() => {
    if (isSuccess && categories) {
      dispatch(categoryMappingActions.setCategories(categories));
    }
  }, [isSuccess, categories, dispatch]);

  const idToSlug = useCallback(
    (id: number): string | undefined => idToSlugMap[id],
    [idToSlugMap]
  );

  const slugToId = useCallback(
    (slug: string): number | undefined => slugToIdMap[slug],
    [slugToIdMap]
  );

  const idsToSlugs = useCallback(
    (ids: number[]): string[] => idsToSlugsUtil(idToSlugMap, ids),
    [idToSlugMap]
  );

  const slugsToIds = useCallback(
    (slugs: string[]): number[] => slugsToIdsUtil(slugToIdMap, slugs),
    [slugToIdMap]
  );

  return {
    idToSlug,
    slugToId,
    idsToSlugs,
    slugsToIds,
    isReady,
  };
}
